import { getSceneState } from "../../stores/sceneStore";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { pruneStaleSelectionForScene } from "../../utils/pruneStaleSelection";
import { SATELLITE_Y_GAP, BASE_LEVEL_GAP, MIN_LEVEL_SPACING } from "./constants";

/**
 * After scene mutations from the graph, primary inspector selection can still reference removed
 * entities; clear it so viewport "visible while selected" overrides do not stick.
 */
export function prunePrimarySelectionIfStaleAfterSceneGraphMutation() {
  pruneStaleSelectionForScene(getSceneState());
}

// ─── shape helpers ────────────────────────────────────────────────────────────
export const estimateLabelWidth = (label) => (label?.length ?? 0) * 7.2;

export const countSubElements = (body) => {
  let n = 0;
  if (Array.isArray(body.geoms)) n += body.geoms.filter((g) => g != null).length;
  if (Array.isArray(body.sites)) n += body.sites.length;
  if (body.inertia) n += 1;
  return n;
};

// How far right the body satellite column starts, clearing the body's label text
export const getSatXOffset = (bodyName) => {
  const labelRightEdge = 35 + estimateLabelWidth(bodyName);
  return Math.max(80, labelRightEdge + 20);
};

// How far right actuator satellites sit relative to a joint node center
// (joint label starts at r+6 = 17px, not 35px like body labels)
export const getJointActuatorXOffset = (jointName) => {
  const labelRightEdge = 17 + estimateLabelWidth(jointName);
  return Math.max(55, labelRightEdge + 15);
};

// Half-height of a body's satellite column (how far it extends above/below center)
export const getSatHalfHeight = (body) => {
  const n = countSubElements(body);
  return n > 1 ? ((n - 1) * SATELLITE_Y_GAP) / 2 : 0;
};

// Vertical spacing needed between a parent and its children so their satellite
// columns don't overlap each other
export const getLevelSpacing = (parentBody, children) => {
  const parentHalf = getSatHalfHeight(parentBody);
  const maxChildHalf = children.length > 0 ? Math.max(...children.map(getSatHalfHeight)) : 0;
  return Math.max(MIN_LEVEL_SPACING, parentHalf + BASE_LEVEL_GAP + maxChildHalf);
};

// Truncate a label for satellite display so the column stays compact
export const truncateLabel = (str, max = 18) =>
  str && str.length > max ? str.slice(0, max - 1) + "…" : (str ?? "");

// Estimated width of one body's full footprint (body circle + label + satellite column)
export const estimateBodyWidth = (body) => {
  const satOffset = getSatXOffset(body.name);
  const bodyLabelRight = 35 + estimateLabelWidth(body.name);
  const leftExtent = 25;
  if (countSubElements(body) === 0) {
    return leftExtent + bodyLabelRight + 10;
  }
  // satellite label (truncated at 18 chars ≈ 117px) starts at satOffset + r(9) + 5
  const satRight = satOffset + 9 + 5 + 18 * 6.5;
  return leftExtent + Math.max(bodyLabelRight + 10, satRight);
};

// ─── runtime / pose helpers ──────────────────────────────────────────────────
export function findModelGroupInObjectRegistry() {
  const reg = RuntimeRegistry.getAll() || {};
  for (const obj of Object.values(reg)) {
    if (!obj || typeof obj !== "object") continue;
    if (obj.name === "modelGroup") return obj;
    if (typeof obj.getObjectByName === "function") {
      const found = obj.getObjectByName("modelGroup");
      if (found) return found;
    }
  }
  return null;
}

export function localPoseFromWorldRelativeToBody(worldPos, worldQuat, bodyWorldPos, bodyWorldQuat) {
  const invBodyQuat = bodyWorldQuat.clone().invert();
  const localPos = worldPos.clone().sub(bodyWorldPos).applyQuaternion(invBodyQuat);
  const localQuat = invBodyQuat.multiply(worldQuat.clone()).normalize();
  return {
    position: [localPos.x, localPos.y, localPos.z],
    orientation: [localQuat.w, localQuat.x, localQuat.y, localQuat.z],
  };
}

// ─── signatures / descendant collection ──────────────────────────────────────
export const buildSceneGraphBodiesSignature = (bodyMap = {}) => {
  try {
    return JSON.stringify(
      Object.values(bodyMap)
        .filter(Boolean)
        .map((body) => ({
          uuid: body.uuid || null,
          name: body.name || "",
          children: Array.isArray(body.children) ? [...body.children].sort() : [],
          joints: Array.isArray(body.joints) ? [...body.joints].sort() : [],
          sites: Array.isArray(body.sites) ? [...body.sites].sort() : [],
          inertia: body.inertia
            ? {
                uuid: body.inertia.uuid || null,
                name: body.inertia.name || "",
              }
            : null,
          geoms: Array.isArray(body.geoms)
            ? body.geoms.filter(Boolean).map((geom) => ({
                uuid: geom.uuid || null,
                name: geom.name || "",
                kind: geom.kind || "",
                mesh: geom.mesh || "",
                visualOnly: geom.visualOnly === true,
              }))
            : [],
        }))
        .sort((a, b) => String(a.uuid || "").localeCompare(String(b.uuid || "")))
    );
  } catch {
    return "";
  }
};

export const collectDescendantBodyUuids = (sourceBodyUuids, bodies) => {
  const descendants = new Set();
  const stack = [...sourceBodyUuids];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current || descendants.has(current)) continue;
    descendants.add(current);
    const body = bodies?.[current];
    (body?.children || []).forEach((childUuid) => {
      if (childUuid) stack.push(childUuid);
    });
  }
  return descendants;
};
